import type { ReactElement } from 'react'
/**
 * Renders the absolutely-positioned layer of node tiles and dots over the map.
 *
 * Pure presentation: receives memoised data and event handlers from the parent,
 * delegates per-node rendering to {@link TileNode} or {@link DotNode}.
 */

import type { Importance, LinkCreationState } from '../../types/infrastructure-map'
import type { CanvasPosition } from '../../types/infrastructure-map'
import { DotNode, TileNode } from './canvas'
import type { RenderableNode } from './infrastructure-map-canvas.helpers'

interface CanvasEndpoint {
  hostname: string
  ipAddresses?: string[] | null
  deviceType?: string | null
  roles?: string[] | null
  importance?: string | null
}

interface CanvasNodesLayerProps {
  nodes: RenderableNode[]
  getEndpointById: (id: string) => CanvasEndpoint | undefined
  selectedEndpointIds: Set<string>
  taggedEndpointIds: Set<string>
  linkCreationState: LinkCreationState
  viewportZoom: number
  onNodeClick: (endpointId: string, e?: React.MouseEvent) => void
  onNodeContextMenu: (endpointId: string, e: React.MouseEvent) => void
  onNodeDrag: (endpointId: string, position: CanvasPosition) => void
  onNodeDragEnd: (endpointId: string, position: CanvasPosition) => Promise<void>
}

export function CanvasNodesLayer({
  nodes,
  getEndpointById,
  selectedEndpointIds,
  taggedEndpointIds,
  linkCreationState,
  viewportZoom,
  onNodeClick,
  onNodeContextMenu,
  onNodeDrag,
  onNodeDragEnd,
}: CanvasNodesLayerProps): ReactElement {
  return (
    <div className="absolute inset-0" style={{ pointerEvents: 'none' }}>
      {nodes.map((node) => {
        const endpoint = getEndpointById(node.endpointId)
        if (!endpoint) return null
        const isSelected = selectedEndpointIds.has(node.endpointId)
        const isLinkSource = computeIsLinkSource(linkCreationState, node.endpointId)
        const isInCollectionSet = taggedEndpointIds.has(node.endpointId)
        if (node.displayType === 'dot') {
          return (
            <DotNodeRender
              key={node.endpointId}
              node={node}
              endpoint={endpoint}
              isSelected={isSelected}
              isInCollectionSet={isInCollectionSet}
              viewportZoom={viewportZoom}
              onNodeClick={onNodeClick}
              onNodeContextMenu={onNodeContextMenu}
            />
          )
        }
        return (
          <TileNodeRender
            key={node.endpointId}
            node={node}
            endpoint={endpoint}
            isSelected={isSelected}
            isLinkSource={isLinkSource}
            isInCollectionSet={isInCollectionSet}
            viewportZoom={viewportZoom}
            onNodeClick={onNodeClick}
            onNodeContextMenu={onNodeContextMenu}
            onNodeDrag={onNodeDrag}
            onNodeDragEnd={onNodeDragEnd}
          />
        )
      })}
    </div>
  )
}

function computeIsLinkSource(state: LinkCreationState, endpointId: string): boolean {
  if (!state.isActive) return false
  if (state.parentEndpointId === endpointId) return true
  return state.childEndpointIds.includes(endpointId)
}

interface DotNodeRenderProps {
  node: RenderableNode
  endpoint: CanvasEndpoint
  isSelected: boolean
  isInCollectionSet: boolean
  viewportZoom: number
  onNodeClick: (endpointId: string, e?: React.MouseEvent) => void
  onNodeContextMenu: (endpointId: string, e: React.MouseEvent) => void
}

function DotNodeRender({
  node,
  endpoint,
  isSelected,
  isInCollectionSet,
  viewportZoom,
  onNodeClick,
  onNodeContextMenu,
}: DotNodeRenderProps): ReactElement {
  return (
    <DotNode
      endpointId={node.endpointId}
      hostname={endpoint.hostname}
      ipAddress={endpoint.ipAddresses?.[0] ?? ''}
      position={node.position}
      scale={viewportZoom}
      isSelected={isSelected}
      isInCollectionSet={isInCollectionSet}
      onClick={(e) => {
        onNodeClick(node.endpointId, e)
      }}
      onContextMenu={(e) => {
        onNodeContextMenu(node.endpointId, e)
      }}
    />
  )
}

interface TileNodeRenderProps extends DotNodeRenderProps {
  isLinkSource: boolean
  onNodeDrag: (endpointId: string, position: CanvasPosition) => void
  onNodeDragEnd: (endpointId: string, position: CanvasPosition) => Promise<void>
}

function TileNodeRender({
  node,
  endpoint,
  isSelected,
  isLinkSource,
  isInCollectionSet,
  viewportZoom,
  onNodeClick,
  onNodeContextMenu,
  onNodeDrag,
  onNodeDragEnd,
}: TileNodeRenderProps): ReactElement {
  return (
    <TileNode
      endpointId={node.endpointId}
      hostname={endpoint.hostname}
      ipAddress={endpoint.ipAddresses?.[0] ?? ''}
      deviceType={endpoint.deviceType ?? 'unknown'}
      role={endpoint.roles?.[0]}
      tileSize={node.tileSize}
      importance={(endpoint.importance as Importance | undefined) ?? 'low'}
      isSelected={isSelected}
      isLinkSource={isLinkSource}
      isInCollectionSet={isInCollectionSet}
      position={node.position}
      scale={viewportZoom}
      onDrag={(pos) => {
        onNodeDrag(node.endpointId, pos)
      }}
      onDragEnd={(pos) => {
        void onNodeDragEnd(node.endpointId, pos)
      }}
      onClick={(e) => {
        onNodeClick(node.endpointId, e)
      }}
      onContextMenu={(e) => {
        onNodeContextMenu(node.endpointId, e)
      }}
    />
  )
}
